import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { campaignAPI } from "../services/api";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 1400px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: "Prompt", sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9ca3af;

  &:hover {
    color: #6b7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormField = styled.div``;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #404e67;
  display: block;
  margin-bottom: 0.5rem;
`;

const RequiredStar = styled.span`
  color: #ef4444;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

const SubmitButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 3rem;
  font-size: 14px;
  cursor: pointer;
  flex: 1;

  &:hover {
    background-color: #009a9d;
  }
`;

const EditMemberModal = ({ isOpen, onClose, onSave, memberData }) => {
  const [formData, setFormData] = useState({
    prefix: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "ชาย",
    idCard: "",
    birthDate: "",
    lineUid: "",
    company: "",
    department: "",
    position: "",
    isCompanyAdmin: false,
    username: "",
    password: "",
  });
  const [companiesList, setCompaniesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await campaignAPI.getList(1, "");

      if (response.result && response.list && Array.isArray(response.list)) {
        const companies = response.list
          .filter((item) => item.name && item.name !== "-")
          .map((item) => ({
            id: item.id,
            name: item.name,
          }));

        const uniqueCompanies = companies.filter(
          (company, index, self) =>
            index === self.findIndex((c) => c.name === company.name)
        );

        setCompaniesList(uniqueCompanies);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (memberData && isOpen && companiesList.length > 0) {
      const matchedCompany = companiesList.find(
        (c) => c.name === memberData.memberCompany
      );
      const primaryCampaignId = matchedCompany
        ? String(matchedCompany.id)
        : String(memberData.memberCampaign?.[0]?.campaignid || "");

      let displayBirthDate = memberData.birthday || "";
      if (memberData.birthday) {
        const dateParts = memberData.birthday.split("-");
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0]);
          if (year < 2500) {
            const buddhistYear = year + 543;
            displayBirthDate = `${buddhistYear}-${dateParts[1]}-${dateParts[2]}`;
          }
        }
      }

      setFormData({
        prefix: memberData.nameprefix || "",
        firstName: memberData.name || "",
        lastName: memberData.lastname || "",
        email: memberData.email || "",
        phone: memberData.tel || "",
        gender: memberData.gender === "1" ? "ชาย" : "หญิง",
        idCard: memberData.nid || "",
        birthDate: displayBirthDate,
        lineUid: memberData.lineuid || "",
        company: primaryCampaignId,
        department: memberData.position || "", // position field จาก backend = แผนก
        position: memberData.job || "", // job field จาก backend = ตำแหน่งงาน
        isCompanyAdmin: memberData.level === "2",
        username: memberData.username || "",
        password: "",
      });
    }
  }, [memberData, isOpen, companiesList]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };


  const handleSubmit = () => {
    if (onSave && memberData) {
      let birthdayForAPI = formData.birthDate || "2000-01-01";
      if (formData.birthDate) {
        const dateParts = formData.birthDate.split("-");
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0]);
          // ถ้าปี > 2500 แสดงว่าเป็น พ.ศ. ต้องแปลงเป็น ค.ศ. โดยลบ 543
          if (year > 2500) {
            const gregorianYear = year - 543;
            birthdayForAPI = `${gregorianYear}-${dateParts[1]}-${dateParts[2]}`;
          } else {
            birthdayForAPI = formData.birthDate;
          }
        }
      }

      const apiData = {
        id: memberData.id,
        username: formData.username || memberData.username,
        password: formData.password || "",
        nameprefix: formData.prefix,
        name: formData.firstName,
        lastname: formData.lastName,
        email: formData.email || "",
        tel: formData.phone || "0",
        nid: formData.idCard || "0",
        birthday: birthdayForAPI,
        gender: formData.gender === "ชาย" ? "1" : "0",
        lineuid: formData.lineUid || "",
        campaignid: formData.company || "0",
        level: formData.isCompanyAdmin ? "2" : "0",
        position: formData.department || "", // แผนก → ส่งเป็น position field
        job: formData.position || "", // ตำแหน่งงาน → ส่งเป็น job field
        mddateadd: memberData.mddateadd || "",
      };

      onSave(apiData);

      localStorage.setItem("memberDataUpdated", Date.now().toString());
      window.dispatchEvent(new CustomEvent("memberDataUpdated"));
    } else {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>แก้ไขสมาชิก</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGrid>
            <FormField>
              <Label>
                คำนำหน้าชื่อ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.prefix}
                onChange={(e) => handleInputChange("prefix", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                ชื่อ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                นามสกุล <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>อีเมล</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                โทร <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 10) {
                    handleInputChange("phone", value);
                  }
                }}
                maxLength={10}
                placeholder="เบอร์โทร 10 หลัก"
              />
            </FormField>

            <FormField>
              <Label>เพศ</Label>
              <Select
                value={formData.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
              >
                <option value="ชาย">ชาย</option>
                <option value="หญิง">หญิง</option>
              </Select>
            </FormField>

            <FormField>
              <Label>
                เลขบัตรประชาชน/เลขบัตรประกันสังคม/passport สำหรับชาวต่างชาติ
                <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.idCard}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 13) {
                    handleInputChange("idCard", value);
                  }
                }}
                maxLength={13}
                placeholder="เลขบัตรประชาชน 13 หลัก"
              />
            </FormField>

            <FormField>
              <Label>วันเกิด (พ.ศ.)</Label>
              <Input
                type="text"
                value={
                  formData.birthDate
                    ? formData.birthDate.split("-").reverse().join("/")
                    : ""
                }
                onChange={(e) => {
                  let value = e.target.value.replace(/[^\d/]/g, "");

                  if (value.length > 10) {
                    value = value.substring(0, 10);
                  }

                  // แปลงกลับเป็น YYYY-MM-DD สำหรับเก็บใน state
                  const parts = value.split("/");
                  if (
                    parts.length === 3 &&
                    parts[0].length === 2 &&
                    parts[1].length === 2 &&
                    parts[2].length === 4
                  ) {
                    const day = parts[0];
                    const month = parts[1];
                    const year = parts[2];
                    handleInputChange("birthDate", `${year}-${month}-${day}`);
                  } else {
                    const currentDate = formData.birthDate.split("-");
                    if (parts[0]) currentDate[2] = parts[0].padStart(2, "0");
                    if (parts[1]) currentDate[1] = parts[1].padStart(2, "0");
                    if (parts[2]) currentDate[0] = parts[2];
                    handleInputChange("birthDate", currentDate.join("-"));
                  }
                }}
                placeholder="วว/ดด/ปปปป (เช่น 01/01/2545)"
                maxLength={10}
              />
            </FormField>

            <FormField>
              <Label>uid Line application</Label>
              <Input
                type="text"
                value={formData.lineUid}
                onChange={(e) => handleInputChange("lineUid", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>บริษัท</Label>
              <select
                value={formData.company}
                onChange={(e) => handleInputChange("company", e.target.value)}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  background: "white",
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "#111827",
                }}
              >
                <option value="">
                  {loading ? "กำลังโหลด..." : "เลือกบริษัท"}
                </option>
                {companiesList.map((company) => (
                  <option key={company.id} value={String(company.id)}>
                    {company.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField>
              <Label>&nbsp;</Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "0.5rem",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#374151",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.isCompanyAdmin}
                    onChange={(e) =>
                      handleInputChange("isCompanyAdmin", e.target.checked)
                    }
                    style={{
                      width: "1rem",
                      height: "1rem",
                      cursor: "pointer",
                      accentColor: "#00B5B8",
                    }}
                  />
                  admin บริษัท
                </label>
              </div>
            </FormField>

            <FormField>
              <Label>
                ชื่อผู้ใช้ (Username) <RequiredStar>*</RequiredStar>
              </Label>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <span
                  style={{
                    padding: "0.75rem",
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRight: "none",
                    borderRadius: "6px 0 0 6px",
                    fontSize: "14px",
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  MFU
                </span>
                <Input
                  type="text"
                  value={formData.username || ""}
                  onChange={(e) => handleInputChange("username", e.target.value)}
                  style={{ borderRadius: "0 6px 6px 0" }}
                />
              </div>
            </FormField>

            <FormField>
              <Label>แผนก</Label>
              <Input
                type="text"
                value={formData.department}
                onChange={(e) =>
                  handleInputChange("department", e.target.value)
                }
              />
            </FormField>

            <FormField></FormField>

            <FormField>
              <Label>
                รหัสผ่าน (Password) <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="password"
                value={formData.password || ""}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="ปล่อยว่างถ้าไม่ต้องการเปลี่ยน"
              />
            </FormField>

            <FormField>
              <Label>ตำแหน่งงาน</Label>
              <Input
                type="text"
                value={formData.position}
                onChange={(e) => handleInputChange("position", e.target.value)}
              />
            </FormField>

            <FormField></FormField>
            <FormField></FormField>
          </FormGrid>

          <ButtonContainer>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SubmitButton onClick={handleSubmit}>บันทึก</SubmitButton>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default EditMemberModal;
